import Cookies from 'js-cookie';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { COMMONS, DATE_FORMAT } from 'globalConstants';
import {
  OperationInDateParamsType,
  DatesDiffType,
  FormatTimeStampType,
  CompareDatesType,
} from 'types/dateHelpers';
import { PandaRowDataType } from 'types/pandaTransferListWidget';
import { RootStateType } from 'types/store';

const { YYYY_MM_DD } = DATE_FORMAT;
const { ADD, SUBTRACT } = COMMONS;

const FALLBACK_TIMEZONE = Cookies.get('tenant-timezone') || 'America/Los_Angeles';

function shouldUseLocalTimezone() {
  return Cookies.get('use-local-timezone') === 'true';
}

export const useDateHelpers = () => {
  const [currentSettings] = useSelector(
    (state: RootStateType) => [state.current.settings],
    shallowEqual
  );

  const DEFAULT_TIME_ZONE = useMemo(() => {
    if (shouldUseLocalTimezone()) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    const timeZoneFromStore = currentSettings?.find(
      (indvSetting: PandaRowDataType) => indvSetting?.name === 'timezone'
    )?.value;

    const checkTimeZone = DateTime.fromObject({}, { zone: timeZoneFromStore });

    if (timeZoneFromStore && checkTimeZone.isValid) {
      return timeZoneFromStore;
    }
    return FALLBACK_TIMEZONE;
  }, [currentSettings]);

  const getDatesDiff: DatesDiffType = (date1, date2, diffBy = 'days') => {
    const start =
      typeof date1 === 'string' ? DateTime.fromISO(date1) : DateTime.fromISO(date1.toISOString());
    const end =
      typeof date2 === 'string' ? DateTime.fromISO(date2) : DateTime.fromISO(date2.toISOString());
    const dateDiff = end.diff(start, diffBy);
    return dateDiff.toObject();
  };

  const operationInDate = (parmas: OperationInDateParamsType) => {
    const {
      dateTime,
      duration = 0,
      optBy = 'days',
      format = YYYY_MM_DD,
      optType = ADD,
      timeZone,
    } = parmas;
    if (!dateTime) {
      return '';
    }

    const tempDate =
      typeof dateTime === 'string'
        ? DateTime.fromISO(dateTime)
        : DateTime.fromISO(dateTime.toISOString());

    switch (optType) {
      case ADD:
        return timeZone
          ? tempDate
              .plus({ [optBy]: duration })
              .setZone(DEFAULT_TIME_ZONE)
              .toFormat(format)
          : tempDate.plus({ [optBy]: duration }).toFormat(format);
      case SUBTRACT:
        return timeZone
          ? tempDate
              .minus({ [optBy]: duration })
              .setZone(DEFAULT_TIME_ZONE)
              .toFormat(format)
          : tempDate.minus({ [optBy]: duration }).toFormat(format);
      default:
        return '';
    }
  };

  const getFormattedTimeStamp: FormatTimeStampType = (
    timeStamp,
    format = YYYY_MM_DD,
    timeZone = DEFAULT_TIME_ZONE
  ) => {
    if (!timeStamp) {
      return '';
    }
    if (timeZone && !shouldUseLocalTimezone()) {
      return DateTime.fromSeconds(timeStamp).setZone(timeZone).toFormat(format);
    } else {
      return DateTime.fromSeconds(timeStamp).toFormat(format);
    }
  };

  const getDateTimeInUnix = (jsDateValue: DateTime) => {
    return DateTime.fromObject(
      {
        year: jsDateValue.year,
        month: jsDateValue.month,
        day: jsDateValue.day,
        hour: jsDateValue.hour,
        minute: jsDateValue.minute,
        second: jsDateValue.second,
      },
      { zone: DEFAULT_TIME_ZONE }
    ).toUnixInteger();
  };

  const getTodayDateTime = (format = YYYY_MM_DD, timeZone = DEFAULT_TIME_ZONE) => {
    if (timeZone) {
      return DateTime.now().setZone(timeZone).toFormat(format);
    } else {
      return DateTime.now().toFormat(format);
    }
  };

  const getTodayJSDate = (timeZone = DEFAULT_TIME_ZONE) => {
    if (timeZone && !shouldUseLocalTimezone()) {
      // Get today's date in the specified timezone
      const todayInTimezone = DateTime.now().setZone(timeZone);
      // Create a local Date object with the same year, month, day
      return new Date(todayInTimezone.year, todayInTimezone.month - 1, todayInTimezone.day);
    } else {
      return DateTime.now()?.toJSDate();
    }
  };

  const compareDates: CompareDatesType = (
    date1,
    date2,
    compBy = 'day',
    compType = 'EQUAL'
  ): boolean => {
    const d1 = DateTime.fromISO(date1).startOf(compBy);
    const d2 = DateTime.fromISO(date2).startOf(compBy);

    switch (compType) {
      case 'EQUAL':
        return d2.equals(d1);
      case 'LESSTHAN':
        return d2 < d1;
      case 'GREATERTHAN':
        return d2 > d1;
      case 'LESSTHANEQUALTO':
        return d2 <= d1;
      case 'GREATERTHANEQUALTO':
        return d2 >= d1;
      default:
        return d2.equals(d1);
    }
  };

  return {
    DEFAULT_TIME_ZONE,
    getDatesDiff,
    getTodayJSDate,
    operationInDate,
    getTodayDateTime,
    getDateTimeInUnix,
    getFormattedTimeStamp,
    compareDates,
  };
};
